type Rgb = [number, number, number];

function cssColorToRgb(input: string): Rgb | null {
  const s = String(input || "").trim();
  if (!s) return null;
  if (s[0] === "#") {
    const hex = s.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
      ];
    }
    if (hex.length >= 6) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
  }
  const m = s.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}

function rgbCss(rgb: Rgb, a = 1): string {
  return `rgba(${Math.round(rgb[0])},${Math.round(rgb[1])},${Math.round(rgb[2])},${a})`;
}

function mix(rgb: Rgb, t: number, toward: "light" | "dark"): Rgb {
  const k = Math.max(0, Math.min(1, t));
  if (toward === "light") {
    return [
      rgb[0] + (255 - rgb[0]) * k,
      rgb[1] + (255 - rgb[1]) * k,
      rgb[2] + (255 - rgb[2]) * k,
    ];
  }
  return [rgb[0] * (1 - k), rgb[1] * (1 - k), rgb[2] * (1 - k)];
}

function isOwnBubble(el: HTMLElement): boolean {
  if (el.querySelector(".primary-gradient, .bg-primary")) return true;
  if (el.querySelector(".bg-otherMessageBg")) return false;
  let n: HTMLElement | null = el;
  for (let i = 0; i < 7 && n; i++) {
    if (n.classList.contains("items-end")) return true;
    n = n.parentElement;
  }
  return false;
}

function paintTarget(el: HTMLElement): HTMLElement {
  return (
    (el.querySelector(
      ".primary-gradient, .bg-otherMessageBg, .bg-primary",
    ) as HTMLElement) ||
    (el.firstElementChild as HTMLElement) ||
    el
  );
}

function resolveBubbleFill(el: HTMLElement): Rgb {
  const paint = paintTarget(el);
  const root = getComputedStyle(document.documentElement);
  const own = isOwnBubble(el);
  const fromVar = root
    .getPropertyValue(own ? "--theme-color" : "--other-message-bg")
    .trim();
  const fromPaint = getComputedStyle(paint).backgroundColor;
  return (
    cssColorToRgb(fromVar) ||
    cssColorToRgb(fromPaint) ||
    (own ? [252, 198, 4] : [221, 221, 221])
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

/**
 * Wave left→right: remaining bubble is drawn on a large canvas,
 * dust takes the bubble/theme fill and flies out past the edges.
 */
export function runThanosSnap(el: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const paint = paintTarget(el);
    const rect = paint.getBoundingClientRect();
    if (rect.width < 4 || rect.height < 4) {
      resolve();
      return;
    }

    const fill = resolveBubbleFill(el);
    const palette = [
      rgbCss(fill, 1),
      rgbCss(mix(fill, 0.16, "light"), 1),
      rgbCss(mix(fill, 0.08, "dark"), 1),
      rgbCss(mix(fill, 0.28, "light"), 1),
      rgbCss(mix(fill, 0.18, "dark"), 1),
    ];

    const PAD = 200;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const viewW = rect.width + PAD * 2;
    const viewH = rect.height + PAD * 2;

    const overlay = document.createElement("canvas");
    overlay.width = Math.max(1, Math.ceil(viewW * dpr));
    overlay.height = Math.max(1, Math.ceil(viewH * dpr));
    overlay.style.cssText = [
      "position:fixed",
      `left:${rect.left - PAD}px`,
      `top:${rect.top - PAD}px`,
      `width:${viewW}px`,
      `height:${viewH}px`,
      "pointer-events:none",
      "z-index:2147483000",
    ].join(";");
    document.body.appendChild(overlay);
    const ctx = overlay.getContext("2d");
    if (!ctx) {
      overlay.remove();
      resolve();
      return;
    }
    ctx.scale(dpr, dpr);

    const cols = Math.max(12, Math.min(32, Math.floor(rect.width / 5)));
    const rows = Math.max(8, Math.min(24, Math.floor(rect.height / 5)));
    const pw = rect.width / cols;
    const ph = rect.height / rows;

    type Particle = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      delay: number;
      w: number;
      h: number;
      rot: number;
      vr: number;
      c: string;
    };
    const particles: Particle[] = [];
    let n = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const ang = -0.9 + Math.random() * 1.8;
        const speed = 70 + Math.random() * 170;
        particles.push({
          x: PAD + c * pw + Math.random() * pw * 0.35,
          y: PAD + r * ph + Math.random() * ph * 0.35,
          vx: Math.cos(ang) * speed + 40,
          vy: Math.sin(ang) * speed - 20,
          delay: (c / cols) * 0.3 + Math.random() * 0.03,
          w: Math.max(2.2, pw * (0.45 + Math.random() * 0.55)),
          h: Math.max(2.2, ph * (0.45 + Math.random() * 0.55)),
          rot: Math.random() * Math.PI,
          vr: -5 + Math.random() * 10,
          c: palette[n++ % palette.length],
        });
      }
    }

    const prevVis = el.style.visibility;
    el.style.visibility = "hidden";

    const t0 = performance.now();
    const WAVE_MS = 380;
    const DUST_S = 0.62;
    const TOTAL_MS = 880;
    let last = t0;
    const radius = Math.min(12, rect.width / 4, rect.height / 2);

    const finish = () => {
      overlay.remove();
      el.style.visibility = prevVis;
      resolve();
    };

    const tick = (now: number) => {
      const t = now - t0;
      const dt = Math.min(0.032, (now - last) / 1000);
      last = now;
      const wave = Math.min(1, t / WAVE_MS);
      const cut = rect.width * wave;

      ctx.clearRect(0, 0, viewW, viewH);

      const remain = rect.width - cut;
      if (remain > 0.6) {
        ctx.save();
        ctx.fillStyle = rgbCss(fill, 1);
        roundRect(ctx, PAD + cut, PAD, remain, rect.height, radius);
        ctx.fill();
        ctx.restore();
      }

      const sec = t / 1000;
      for (const p of particles) {
        const local = sec - p.delay;
        if (local < 0) continue;
        p.vy += 95 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        const life = Math.max(0, 1 - local / DUST_S);
        if (life <= 0) continue;
        ctx.save();
        ctx.globalAlpha = life * 0.95;
        ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (t < TOTAL_MS) requestAnimationFrame(tick);
      else finish();
    };

    requestAnimationFrame(tick);
  });
}
